import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import type { AiCodeProgressEvent } from '~/services/PromoVoucherAiService';

type Props = {
  events: Array<AiCodeProgressEvent>;
  includeSave?: boolean;
  validationPassed?: boolean;
};

const steps: Array<{ step: AiCodeProgressEvent['step']; label: string }> = [
  { step: 'identity', label: 'Code identity checked' },
  { step: 'metadata', label: 'Promo Meta loaded' },
  { step: 'generation', label: 'AI response received' },
  { step: 'payload', label: 'AI response checked' },
  { step: 'validation', label: 'Validation passed' },
  { step: 'write', label: 'Write completed' },
  { step: 'readback', label: 'Saved record read back' },
  { step: 'check', label: 'Saved code checked' },
];

export default function AiWorkflowProgress({ events, includeSave = false, validationPassed }: Props) {
  const latestByStep = new Map(events.map((event) => [event.step, event]));

  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-live="polite">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Progress
      </Typography>
      <Stack spacing={1}>
        {steps.slice(0, includeSave ? steps.length : 5).map(({ step, label }) => {
          const event = latestByStep.get(step);
          const isSkipped = event?.status === 'skipped';
          const isDone = event?.status === 'done' && (step !== 'validation' || validationPassed !== false);
          const isRunning = event?.status === 'running';
          const isFailed = event?.status === 'failed' || (step === 'validation' && event?.status === 'done' && !isDone);
          return (
            <Stack key={step} direction="row" alignItems="center" spacing={1}>
              {isRunning ? (
                <CircularProgress size={18} aria-label={`${label} in progress`} />
              ) : isDone ? (
                <CheckCircleOutlineIcon color="success" fontSize="small" />
              ) : (
                <RadioButtonUncheckedIcon color={isFailed ? 'error' : 'disabled'} fontSize="small" />
              )}
              <Box>
                <Typography variant="body2" color={isFailed ? 'error' : 'text.primary'}>
                  {isSkipped ? 'Pre-save check unavailable' : label}
                </Typography>
                {event && (
                  <Typography variant="caption" color="text.secondary">
                    {event.detail}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
